import { resources } from "@constants/hardcoded-strings";
import {
    AXNUIContent,
    AXNUITheme
} from "@iddataweb/idw-common-ui/types";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { ThemingPolicyRetryMessageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { getPolicyTypeById } from "@utils/policies/policy-util";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { PolicyTypes } from "@enums/policy-types";
import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";

export const getThemingPolicyRetryMessageValidationErrors = (
    isUsingThemedUI: boolean,
    axnuiTheme: AXNUITheme,
    axnuiContent: AXNUIContent,
    rpServiceConfiguration: FormattedRpServiceConfiguration
): ThemingPolicyRetryMessageValidationErrors => {
    if (!rpServiceConfiguration) {
        return {
            borderRadiusError: null,
            borderWidthError: null,
            policyConfigDecisionsRetryErrors: {},
            count: 0,
            tabTitle: <TabTitleWithErrorCount
                tabName={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.tabName}
                errorCount={0}
            />
        };
    }

    let count = 0;
    let borderWidthError = null;
    let borderRadiusError = null;
    const { policyConfig, policies } = rpServiceConfiguration;
    const usePolicyManager = isStringTrue(rpServiceConfiguration.usePolicyManager);
    const policyType = getPolicyTypeById(usePolicyManager, policyConfig, policies);
    const policyConfigDecisionsRetryErrors: { [key: string]: React.ReactNode } = {};
    if (isUsingThemedUI) {
        if (!axnuiTheme.policyRetryMessage.cornerRadius) {
            count += 1;
            borderRadiusError = <ErrorAlert><span>This field is required. Please enter a valid Border Radius followed by &quot;px&quot;</span></ErrorAlert>;
        } else if (`${axnuiTheme.policyRetryMessage.cornerRadius}`.includes("-")) {
            count += 1;
            borderRadiusError = <ErrorAlert><span>The Border Radius cannot be negative</span></ErrorAlert>;
        } else if (!`${axnuiTheme.policyRetryMessage.cornerRadius}`.includes("px") || `${axnuiTheme.confirmButton.cornerRadius}`.includes(" ")) {
            count += 1;
            borderRadiusError = <ErrorAlert><span>Please use the format &quot;5px&quot; with no spaces</span></ErrorAlert>;
        } else {
            const numberValue = `${axnuiTheme.policyRetryMessage.cornerRadius}`.split("px")[0];
            if (isNaN(parseInt(numberValue))) {
                count += 1;
                borderRadiusError = <ErrorAlert><span>Please use the format &quot;5px&quot; with no spaces</span></ErrorAlert>
            }
        }
        if (!axnuiTheme.policyRetryMessage.borderWidth) {
            count += 1;
            borderWidthError = <ErrorAlert><span>This field is required. Please enter a valid Border Width followed by &quot;px&quot;</span></ErrorAlert>;
        } else if (`${axnuiTheme.policyRetryMessage.borderWidth}`.includes("-")) {
            count += 1;
            borderWidthError = <ErrorAlert><span>The Border Width cannot be negative</span></ErrorAlert>;
        } else if (!`${axnuiTheme.policyRetryMessage.borderWidth}`.includes("px") || `${axnuiTheme.policyRetryMessage.cornerRadius}`.includes(" ")) {
            count += 1;
            borderWidthError = <ErrorAlert><span>Please use the format &quot;5px&quot; with no spaces</span></ErrorAlert>;
        } else {
            const numberValue = `${axnuiTheme.policyRetryMessage.borderWidth}`.split("px")[0];
            if (isNaN(parseInt(numberValue))) {
                count += 1;
                borderWidthError = <ErrorAlert><span>Please use the format &quot;5px&quot; with no spaces</span></ErrorAlert>
            }
        }

        if (policies.length && policyType !== PolicyTypes.NONE) {
            if (policyConfig !== null) {
                policyConfig.decisions.forEach(decision => {
                    if (decision.policyDecisionType === PolicyDecisionActionTypes.DENY) {
                        let key = decision.uuid;
                        let retryMessageId = axnuiContent.policyDecisionRetryMessage[decision.uuid]?.retryMessage;
                        if (decision.id !== null) {
                            retryMessageId = axnuiContent.policyDecisionRetryMessage[decision.id]?.retryMessage;
                            key = decision.id;
                        }
                        if (retryMessageId === '') {
                            policyConfigDecisionsRetryErrors[key] = (
                                <ErrorAlert key={key}>
                                    <span>{resources.pages.verificationServiceDetails.tabs.policies.sections.details.sections.retryErrorMessage.errors.retryMessage.noRetryMessage}</span>
                                </ErrorAlert>
                            );
                            count++;
                        }
                    }
                });
                if (axnuiContent.policyDecisionRetryMessage['default']?.retryMessage === '') {
                    policyConfigDecisionsRetryErrors['default'] = (
                        <ErrorAlert key={'default'}>
                            <span>{resources.pages.verificationServiceDetails.tabs.policies.sections.details.sections.retryErrorMessage.errors.retryMessage.noRetryMessage}</span>
                        </ErrorAlert>
                    );
                    count++;
                }
            }
        }
    }
    return {
        borderRadiusError,
        borderWidthError,
        policyConfigDecisionsRetryErrors,
        count: count,
        tabTitle: <TabTitleWithErrorCount
            tabName={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.tabName}
            errorCount={count}
        />
    };
};